import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CalendarClock, Trash2 } from "lucide-react";

const fmt2 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const parseLocal = (iso) => { const [y, m, d] = String(iso).split("-").map(Number); return new Date(y, m - 1, d); };
const field = "rounded-none border border-black bg-background h-10 px-3 focus-visible:ring-0 focus-visible:border-black";

export const DealSchedule = ({ dealId, committed, onChange }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [funded, setFunded] = useState("");
  const [feeHoldback, setFeeHoldback] = useState("");
  const [grossReturn, setGrossReturn] = useState("");
  const [investorReturn, setInvestorReturn] = useState("");
  const [termDays, setTermDays] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [computed, setComputed] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [cadence, setCadence] = useState({ daily: true, weekly: true });

  const loadCadence = useCallback(() => {
    adminApi.get(`/site/admin/deals/${dealId}/email-cadence`).then(({ data }) => setCadence(data)).catch(() => {});
  }, [dealId]);
  const toggleCadence = async (key) => {
    const next = { ...cadence, [key]: !cadence[key] };
    setCadence(next);
    try { await adminApi.put(`/site/admin/deals/${dealId}/email-cadence`, next); toast.success("Email cadence saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setCadence(cadence); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/site/admin/deals/${dealId}/schedule`);
      setComputed(data.computed);
      setInvestors(data.investors || []);
      if (data.schedule) {
        const s = data.schedule;
        setFunded(String(s.funded ?? ""));
        setFeeHoldback(String(s.fee_holdback ?? ""));
        setGrossReturn(String(s.gross_return ?? s.total_return ?? ""));
        setInvestorReturn(String(s.investor_return ?? s.total_return ?? ""));
        setTermDays(String(s.term_days ?? ""));
        setStartDate(s.start_date || today);
      } else {
        setFunded(String(committed || ""));
      }
    } catch (err) { /* silent */ }
  }, [dealId, committed, today]);

  useEffect(() => { load(); loadCadence(); }, [load, loadCadence]);

  const save = async () => {
    const f = parseInt(String(funded).replace(/[^0-9]/g, ""), 10);
    const fee = parseInt(String(feeHoldback).replace(/[^0-9]/g, ""), 10) || 0;
    const g = parseInt(String(grossReturn).replace(/[^0-9]/g, ""), 10);
    const ir = parseInt(String(investorReturn).replace(/[^0-9]/g, ""), 10);
    const t = parseInt(String(termDays).replace(/[^0-9]/g, ""), 10);
    if (!f || f <= 0) return toast.error("Enter total funded");
    if (!ir || ir <= 0) return toast.error("Enter the investor total return");
    if (!g || g <= 0) return toast.error("Enter the gross (repayment) return");
    if (!t || t <= 0) return toast.error("Enter term (number of business days)");
    if (!startDate) return toast.error("Pick a start date");
    setSaving(true);
    try {
      await adminApi.put(`/site/admin/deals/${dealId}/schedule`, { funded: f, fee_holdback: fee, gross_return: g, investor_return: ir, total_return: ir, term_days: t, start_date: startDate });
      toast.success("Payment schedule saved");
      await load(); onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the payment schedule for this deal?")) return;
    try {
      await adminApi.delete(`/site/admin/deals/${dealId}/schedule`);
      setComputed(null); setInvestors([]);
      toast.success("Schedule removed"); onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const paidDates = computed ? computed.dates.filter((d) => d <= computed.today).map(parseLocal) : [];
  const upcomingDates = computed ? computed.dates.filter((d) => d > computed.today).map(parseLocal) : [];

  const CadenceBtn = ({ k, label }) => (
    <button data-testid={`cadence-${k}-${dealId}`} onClick={() => toggleCadence(k)} className={`inline-flex items-center gap-2 h-8 px-3 mono text-[9px] uppercase tracking-[0.15em] border border-black transition-colors ${cadence[k] ? "bg-black text-white" : "bg-white text-muted-foreground"}`}>
      <span className={`w-2 h-2 rounded-full ${cadence[k] ? "bg-green-400" : "bg-neutral-400"}`} /> {label} {cadence[k] ? "On" : "Off"}
    </button>
  );

  const Stat = ({ label, value, testid }) => (
    <div className="border border-black p-3">
      <div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div data-testid={testid} className="text-lg font-black tracking-tighter mt-1">{value}</div>
    </div>
  );

  return (
    <div data-testid={`deal-schedule-${dealId}`} className="border-t border-black p-4 space-y-5">
      <div className="flex items-center gap-2">
        <CalendarClock size={15} />
        <h4 className="mono text-xs uppercase tracking-[0.25em]">Payment Schedule</h4>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid={`cadence-row-${dealId}`}>
        <span className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Investor emails for this deal:</span>
        <CadenceBtn k="daily" label="Daily" />
        <CadenceBtn k="weekly" label="Weekly" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">Total Funded (investor capital)</Label>
          <Input data-testid="sched-funded" value={funded} onChange={(e) => setFunded(e.target.value.replace(/[^0-9]/g, ""))} className={`${field} mt-1.5`} placeholder="2000000" inputMode="numeric" />
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">Fee Held Back (from funding)</Label>
          <Input data-testid="sched-fee" value={feeHoldback} onChange={(e) => setFeeHoldback(e.target.value.replace(/[^0-9]/g, ""))} className={`${field} mt-1.5`} placeholder="200000" inputMode="numeric" />
          {funded ? <div className="mono text-[9px] text-muted-foreground mt-1">Net to borrower {fmt2((Number(funded) || 0) - (Number(feeHoldback) || 0))}</div> : null}
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">Term (business days)</Label>
          <Input data-testid="sched-term" value={termDays} onChange={(e) => setTermDays(e.target.value.replace(/[^0-9]/g, ""))} className={`${field} mt-1.5`} placeholder="65" inputMode="numeric" />
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">Gross Return — Admin (repayment)</Label>
          <Input data-testid="sched-gross" value={grossReturn} onChange={(e) => setGrossReturn(e.target.value.replace(/[^0-9]/g, ""))} className={`${field} mt-1.5`} placeholder="3000000" inputMode="numeric" />
          {funded && grossReturn ? <div className="mono text-[9px] text-muted-foreground mt-1">{((Number(grossReturn) || 0) / (Number(funded) || 1)).toFixed(2)}× · drives daily payment</div> : null}
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">Investor Total Return</Label>
          <Input data-testid="sched-investor" value={investorReturn} onChange={(e) => setInvestorReturn(e.target.value.replace(/[^0-9]/g, ""))} className={`${field} mt-1.5`} placeholder="2600000" inputMode="numeric" />
          {funded && investorReturn ? <div className="mono text-[9px] text-muted-foreground mt-1">{((Number(investorReturn) || 0) / (Number(funded) || 1)).toFixed(2)}× · what investors see</div> : null}
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">Start Date</Label>
          <Input data-testid="sched-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`${field} mt-1.5`} />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button data-testid="sched-save" onClick={save} disabled={saving} className="rounded-none border border-black h-10 px-6 mono text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save Schedule"}</button>
        {computed && <button data-testid="sched-clear" onClick={clear} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>}
        <span className="mono text-[10px] text-muted-foreground">Equal daily payments · pays Mon–Fri · updates automatically each day</span>
      </div>

      {computed && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Daily Repayment (borrower)" value={fmt2(computed.daily_repayment)} testid="sched-daily-repayment" />
            <Stat label="Investor Daily (steady)" value={fmt2(computed.per_payment)} testid="sched-investor-daily" />
            <Stat label="Payments Made" value={`${computed.elapsed} / ${computed.term_days}`} testid="sched-progress" />
            <Stat label="Next Payment" value={computed.next_date || "Complete"} testid="sched-next" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Net to Borrower" value={fmt2(computed.net_out)} testid="sched-net-out" />
            <Stat label="Collected to Date" value={fmt2(computed.collected)} testid="sched-collected" />
            <Stat label="Account Balance" value={fmt2(computed.account_balance)} testid="sched-account" />
            <Stat label="Principal Paid In" value={computed.principal_paid_in ? "Yes ✓" : "Not yet"} testid="sched-principal" />
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="border border-black inline-block self-start">
              <Calendar
                defaultMonth={parseLocal(computed.start_date)}
                modifiers={{ paid: paidDates, upcoming: upcomingDates }}
                modifiersClassNames={{ paid: "bg-green-200 text-green-900 font-semibold", upcoming: "bg-blue-100 text-blue-900" }}
              />
              <div className="flex items-center gap-4 px-3 pb-3 mono text-[9px] uppercase tracking-[0.15em]">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-green-200 inline-block" /> Paid</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-blue-100 inline-block" /> Upcoming</span>
              </div>
            </div>

            <div className="flex-1 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">
                    <th className="text-left p-2 font-normal">Investor</th>
                    <th className="text-right p-2 font-normal">% of Deal</th>
                    <th className="text-right p-2 font-normal">Received</th>
                    <th className="text-right p-2 font-normal">Remaining</th>
                    <th className="text-right p-2 font-normal">Current Value</th>
                  </tr>
                </thead>
                <tbody>
                  {investors.length === 0 && <tr><td colSpan={5} className="p-3 text-center mono text-xs text-muted-foreground">No investors in this deal.</td></tr>}
                  {investors.map((v) => (
                    <tr key={v.investment_id} data-testid={`sched-inv-${v.investment_id}`} className="border-b border-border last:border-0">
                      <td className="p-2">
                        <div className="font-medium">{v.investor_name}</div>
                        <div className="mono text-[10px] text-muted-foreground">started {fmt2(v.started_with)} → {fmt2(v.total_return)}</div>
                      </td>
                      <td className="p-2 text-right">{v.share_pct}%</td>
                      <td className="p-2 text-right text-green-700">{fmt2(v.received)}</td>
                      <td className="p-2 text-right">{fmt2(v.remaining)}</td>
                      <td className="p-2 text-right font-semibold">{fmt2(v.current_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
